import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Capability {
  id: number;
  title: string;
  description: string;
  icon: string;
  image: string;
  alt: string;
  applications: string[];
  certifications: string[];
}

const CapabilitiesShowcase = () => {
  const capabilities: Capability[] = [
  {
    id: 1,
    title: 'CNC Precision Machining',
    description: 'Multi-axis CNC machining with tolerances down to ±0.0001" for complex aerospace components requiring absolute precision.',
    icon: 'CogIcon',
    image: "https://images.unsplash.com/photo-1715873482041-725886367a1c",
    alt: 'Industrial CNC machine with precision cutting tool working on metallic aerospace component with coolant spray',
    applications: ['Satellite Structures', 'Engine Components', 'Avionics Housing'],
    certifications: ['AS9100', 'ISO 9001']
  },
  {
    id: 2,
    title: 'Additive Manufacturing',
    description: 'Advanced 3D printing technologies including metal laser sintering and polymer fusion for rapid prototyping and production.',
    icon: 'CubeIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18281263c-1768733758631.png",
    alt: 'Metal 3D printer creating complex aerospace part layer by layer with laser sintering technology in controlled environment',
    applications: ['Rapid Prototyping', 'Complex Geometries', 'Lightweight Structures'],
    certifications: ['NADCAP', 'AS9100']
  },
  {
    id: 3,
    title: 'Sheet Metal Fabrication',
    description: 'Precision sheet metal forming, cutting, and assembly for aerospace enclosures, brackets, and structural components.',
    icon: 'RectangleStackIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1247e180d-1766493775753.png",
    alt: 'Precision sheet metal bending machine forming aerospace-grade aluminum panel with exact angle measurements',
    applications: ['Enclosures', 'Brackets', 'Panels'],
    certifications: ['AS9100', 'ITAR']
  },
  {
    id: 4,
    title: 'Composite Manufacturing',
    description: 'Advanced composite layup and curing processes for lightweight, high-strength aerospace structures and components.',
    icon: 'Square3Stack3DIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12f616565-1766493775701.png",
    alt: 'Carbon fiber composite material being laid up in precision mold for aerospace structural component',
    applications: ['Structural Components', 'Fairings', 'Thermal Protection'],
    certifications: ['AS9100', 'NADCAP']
  }];


  return (
    <section className="relative py-24 bg-gradient-to-b from-background to-primary">
      <div className="mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 rounded-full border border-accent/30 mb-6">
            <Icon name="WrenchScrewdriverIcon" size={20} className="text-accent" />
            <span className="font-cta text-sm text-accent">Manufacturing Excellence</span>
          </div>
          <h2 className="font-headline text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Mission-Critical Capabilities
          </h2>
          <p className="font-body text-lg text-technical-gray">
            Comprehensive aerospace manufacturing services backed by world-class certifications and decades of precision engineering expertise.
          </p>
        </div>

        {/* Capabilities Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {capabilities.map((capability) =>
          <div
            key={capability.id}
            className="group bg-card border border-border rounded-2xl overflow-hidden hover:border-accent transition-all duration-300 hover:shadow-glow">

              {/* Image Section */}
              <div className="relative h-64 overflow-hidden">
                <AppImage
                src={capability.image}
                alt={capability.alt}
                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/60 to-transparent"></div>
                
                {/* Icon Badge */}
                <div className="absolute top-6 left-6 w-16 h-16 bg-accent/20 backdrop-blur-sm rounded-xl border border-accent/30 flex items-center justify-center">
                  <Icon name={capability.icon as any} size={32} className="text-accent" />
                </div>

                {/* Certifications */}
                <div className="absolute bottom-6 left-6 flex flex-wrap gap-2">
                  {capability.certifications.map((cert) =>
                <span
                  key={cert}
                  className="px-3 py-1 bg-aerospace-green/20 backdrop-blur-sm rounded-md border border-aerospace-green/30 font-cta text-xs text-aerospace-green">

                      {cert}
                    </span>
                )}
                </div>
              </div>

              {/* Content Section */}
              <div className="p-6 space-y-4">
                <h3 className="font-headline text-2xl font-bold text-foreground group-hover:text-accent transition-colors duration-300">
                  {capability.title}
                </h3>
                <p className="font-body text-technical-gray leading-relaxed">
                  {capability.description}
                </p>

                {/* Applications */}
                <div className="space-y-2">
                  <p className="font-cta text-xs text-accent uppercase tracking-wider">Key Applications</p>
                  <div className="flex flex-wrap gap-2">
                    {capability.applications.map((app) =>
                  <span
                    key={app}
                    className="px-3 py-1 bg-muted rounded-md border border-border font-body text-sm text-technical-gray">

                        {app}
                      </span>
                  )}
                  </div>
                </div>

                {/* Learn More Link */}
                <Link
                href="/capabilities"
                className="inline-flex items-center space-x-2 text-accent hover:text-mission-orange transition-colors duration-300 group/link">

                  <span className="font-cta text-sm">Learn More</span>
                  <Icon
                  name="ArrowRightIcon"
                  size={16}
                  className="group-hover/link:translate-x-1 transition-transform duration-300" />

                </Link>
              </div>
            </div>
          )}
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <Link
            href="/capabilities"
            className="inline-flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-accent to-mission-orange text-primary font-cta text-base font-medium rounded-lg hover:shadow-glow transition-all duration-300 hover:-translate-y-1">

            <span>EXPLORE ALL CAPABILITIES</span>
            <Icon name="ArrowRightIcon" size={20} />
          </Link>
        </div>
      </div>
    </section>);

};

export default CapabilitiesShowcase;